import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { PartyPopper, CalendarCheck, Briefcase } from 'lucide-react';
import api from '../api/api';
import { useTheme } from '../hooks/useTheme';

/**
 * CelebrateButton — colorful "Celebrate" pill on the student dashboard. Opens a
 * small form (Got an Interview / Got a Job, optional company and role), saves it
 * to the student's wins (POST /user/celebrations), then shows a confetti popup.
 * The confetti is copied from OnboardingCelebration's pattern; the saved wins
 * are listed under My Wins in My Preferences.
 *
 * Props: onCelebrated(counts) — optional, called after a win is saved.
 */

const COLORS = ['#10b981', '#f59e0b', '#f43f5e', '#60a5fa', '#a78bfa'];

const CHOICES = [
    { type: 'interview', label: 'Got an Interview', Icon: CalendarCheck },
    { type: 'job', label: 'Got a Job', Icon: Briefcase },
];

export default function CelebrateButton({ onCelebrated }) {
    const { isDarkMode } = useTheme();
    const [showForm, setShowForm] = useState(false);
    const [type, setType] = useState('');
    const [company, setCompany] = useState('');
    const [jobTitle, setJobTitle] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    // The saved win's type ('interview' | 'job') while the confetti popup shows
    const [celebrated, setCelebrated] = useState(null);
    const reduced = typeof window !== 'undefined' && window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const pieces = useMemo(() => Array.from({ length: reduced ? 0 : 40 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        color: COLORS[i % COLORS.length],
        delay: Math.random() * 0.6,
        duration: 1.8 + Math.random() * 1.4,
        w: 6 + Math.random() * 6,
        rotate: Math.random() * 360,
    })), [reduced]);

    // The popup closes by itself after a few seconds, like OnboardingCelebration
    useEffect(() => {
        if (!celebrated) return;
        const t = setTimeout(() => setCelebrated(null), 4000);
        return () => clearTimeout(t);
    }, [celebrated]);

    const openForm = () => {
        setType('');
        setCompany('');
        setJobTitle('');
        setError('');
        setShowForm(true);
    };

    // Not while saving: the win would still be saved and the confetti would pop up anyway
    const closeForm = () => {
        if (!saving) setShowForm(false);
    };

    const handleCelebrate = async () => {
        setSaving(true);
        setError('');
        try {
            const response = await api.post('/user/celebrations', {
                type,
                company: company.trim(),
                jobTitle: jobTitle.trim(),
            });
            setShowForm(false);
            setCelebrated(response.data?.celebration?.CelebrationType || type);
            if (onCelebrated) {
                onCelebrated(response.data?.counts);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const cardBg = isDarkMode ? 'bg-slate-800' : 'bg-white';
    const heading = isDarkMode ? 'text-slate-100' : 'text-slate-900';
    const sub = isDarkMode ? 'text-slate-400' : 'text-slate-600';
    const inputClass = `w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
        isDarkMode
            ? 'bg-slate-900 border-slate-600 text-slate-100 placeholder-slate-500'
            : 'border-slate-300 text-slate-900 placeholder-slate-400'
    }`;

    return (
        <>
            <button
                type="button"
                onClick={openForm}
                className={`inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-fuchsia-600 via-rose-500 to-orange-500 px-4 py-2 text-sm font-bold text-white shadow-md shadow-rose-500/30 transition-transform motion-safe:hover:scale-105 focus:outline-none focus-visible:ring-4 focus-visible:ring-fuchsia-400/60 focus-visible:ring-offset-2 ${
                    isDarkMode ? 'focus-visible:ring-offset-slate-800' : 'focus-visible:ring-offset-white'
                }`}
            >
                <PartyPopper size={18} />
                Celebrate
            </button>

            {showForm && createPortal(
                <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6">
                    <div
                        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-300"
                        onClick={closeForm}
                    />
                    <div className={`relative w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300 p-6 ${cardBg}`}>
                        <h3 className={`text-lg font-bold text-center ${heading}`}>
                            What are we celebrating?
                        </h3>

                        <div className="mt-5 grid grid-cols-2 gap-3">
                            {CHOICES.map(({ type: value, label, Icon }) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => setType(value)}
                                    aria-pressed={type === value}
                                    className={`flex flex-col items-center gap-2 rounded-2xl border-2 px-3 py-5 text-center text-sm font-semibold transition-colors ${
                                        type === value
                                            ? isDarkMode
                                                ? 'border-fuchsia-400 bg-fuchsia-500/10 text-slate-100'
                                                : 'border-fuchsia-500 bg-fuchsia-50 text-slate-900'
                                            : isDarkMode
                                                ? 'border-slate-600 text-slate-300 hover:bg-slate-700'
                                                : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                                    }`}
                                >
                                    <Icon size={28} className={type === value ? 'text-fuchsia-500' : (isDarkMode ? 'text-slate-400' : 'text-slate-500')} />
                                    {label}
                                </button>
                            ))}
                        </div>

                        <div className="mt-5 space-y-3">
                            <label className="block">
                                <span className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                    Company (optional)
                                </span>
                                <input
                                    type="text"
                                    value={company}
                                    onChange={(e) => setCompany(e.target.value)}
                                    maxLength={200}
                                    className={inputClass}
                                />
                            </label>
                            <label className="block">
                                <span className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                    Role (optional)
                                </span>
                                <input
                                    type="text"
                                    value={jobTitle}
                                    onChange={(e) => setJobTitle(e.target.value)}
                                    maxLength={200}
                                    className={inputClass}
                                />
                            </label>
                        </div>

                        {error && (
                            <p className="mt-3 text-sm text-red-500">{error}</p>
                        )}

                        <div className="mt-6 flex gap-3">
                            <button
                                type="button"
                                onClick={closeForm}
                                disabled={saving}
                                className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 ${
                                    isDarkMode
                                        ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                }`}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleCelebrate}
                                disabled={!type || saving}
                                className="flex-1 rounded-xl bg-gradient-to-r from-fuchsia-600 via-rose-500 to-orange-500 px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {saving ? 'Saving…' : 'Celebrate!'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {celebrated && createPortal(
                <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6">
                    <div
                        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-300"
                        onClick={() => setCelebrated(null)}
                    />

                    <div className={`relative w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300 ${cardBg}`}>
                        <style>{`@keyframes celebrate-fall{0%{transform:translateY(-12vh) rotate(0);opacity:0}8%{opacity:1}100%{transform:translateY(112vh) rotate(720deg);opacity:1}}`}</style>

                        {pieces.map(p => (
                            <span key={p.id} aria-hidden="true" style={{
                                position: 'absolute', top: 0, left: `${p.left}%`,
                                width: p.w, height: p.w * 0.6, background: p.color, borderRadius: 2,
                                transform: `rotate(${p.rotate}deg)`,
                                animation: `celebrate-fall ${p.duration}s linear ${p.delay}s forwards`,
                            }} />
                        ))}

                        <div className="relative text-center px-8 py-12">
                            <motion.div
                                initial={{ scale: 0, rotate: -20 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                                className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-fuchsia-600 via-rose-500 to-orange-500 shadow-lg shadow-rose-500/30"
                            >
                                <PartyPopper size={40} className="text-white" />
                            </motion.div>
                            <motion.h2
                                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                                className={`text-2xl font-extrabold ${heading}`}
                            >
                                {celebrated === 'job' ? 'Congrats on the new job!' : 'Congrats on the interview!'}
                            </motion.h2>
                            <motion.p
                                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }}
                                className={`mt-2 text-sm ${sub}`}
                            >
                                We saved it to your wins in My Preferences.
                            </motion.p>
                            <button
                                type="button"
                                onClick={() => setCelebrated(null)}
                                className={`mt-6 rounded-xl px-6 py-2.5 text-sm font-semibold transition-colors ${
                                    isDarkMode
                                        ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                }`}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}
